import React, { useEffect, useState } from "react"
import {
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  Box,
  useMediaQuery,
} from "@mui/material"
import { Link } from "react-router-dom"
import { styled } from "@mui/system"
import { useAuthUser } from "react-auth-kit"
import api from "../../../utils/api"

const StyledButton = styled(Button)({
  margin: "10px",
})

const operations = [
  {
    header: "Event Admin Operations",
    items: [
      {
        route: "/admin/event-admin/manage-voters",
        label: "Manage Voters",
      },
      // {
      //   route: "/admin/event-admin/instance/create",
      //   label: "Create Voting Instance",
      // },
      // {
      //   route: "/admin/event-admin/instance/${id}/edit",
      //   label: "Edit Instance",
      // },
      // {
      //   route: "/admin/event-admin/instance/${id}/delete",
      //   label: "Delete Instance",
      // },
    ],
  },
]

const NotificationSnackbar = ({
  message,
  onClose,
}: {
  message: string
  onClose: () => void
}) => (
  <Snackbar
    open={!!message}
    autoHideDuration={3000}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "right" }}
  >
    <Alert onClose={onClose} severity="success" sx={{ width: "100%" }}>
      {message}
    </Alert>
  </Snackbar>
)

const useEventData = (eventId: string) => {
  const [eventData, setEventData] = useState<null | {
    name: string
    description: string
    startDate: string
    endDate: string
    //adminLimit: number
    //geofence: string
    logoURL?: string
    numVoters: number
  }>(null)

  const [isLoadingEvent, setIsLoadingEvent] = useState(true)
  const [eventError, setEventError] = useState(null)

  useEffect(() => {
    const fetchEventDetails = async () => {
      setIsLoadingEvent(true)
      try {
        const response = await api.get(`/admin/events/${eventId}`)
        setEventData(response.data)
      } catch (err) {
        // If Axios throws an error, it includes response data in err.response
        const errorMessage =
          err.response?.data?.message || "Failed to get event details."
        setEventError(errorMessage)
        console.error("Error fetching event details:", errorMessage)
      } finally {
        setIsLoadingEvent(false)
      }
    }

    if (eventId) {
      fetchEventDetails()
    }
  }, [eventId])

  return { eventData, isLoadingEvent, eventError }
}

type Props = {
  notification?: string
}

export const EventAdminDashboardPage: React.FC<Props> = ({
  notification = "",
}) => {
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(
    notification
  )
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"))

  const auth = useAuthUser()
  const username = auth()?.username ?? ""
  const name = auth()?.name ?? ""
  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

  const cardWidth = isMobile ? "100%" : "50%"

  const { eventData, isLoadingEvent, eventError } = useEventData(eventId)

  const renderEventDetails = () => (
    <Box paddingBottom={4} display="flex" justifyContent="center">
      <Card
        square
        sx={{ borderRadius: "13px", width: cardWidth, paddingX: "3em" }}
      >
        {eventData?.logoURL && (
          <Box display="flex" justifyContent="center" my={2}>
            <img
              src={eventData.logoURL}
              alt={`${eventData?.name} Logo`}
              style={{ maxWidth: "100%", maxHeight: "200px" }} // You can adjust style as needed
            />
          </Box>
        )}
        <Typography variant="h5" margin={4}>
          {eventData?.name ?? "Loading..."}
        </Typography>
        <Typography variant="body1" mr={3} padding="5px">
          Description: {eventData?.description}
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="body1" mr={3}>
            Start Date: {getFormattedDate(eventData?.startDate)}
          </Typography>
          <Typography variant="body1">
            End Date: {getFormattedDate(eventData?.endDate)}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          sx={{ margin: "auto", marginTop: "10px", marginBottom: "10px" }}
          component={Link}
          to={`/admin/event-admin/event/instance-start`}
        >
          View Instances
        </Button>
      </Card>
    </Box>
  )

  return (
    <Box paddingBottom={4}>
      <Typography variant="h5" m={4}>
        {`Welcome, ${name}!`}
      </Typography>
      {isLoadingEvent ? (
        <CircularProgress />
      ) : eventError ? (
        <Alert severity="error">{eventError}</Alert>
      ) : (
        renderEventDetails()
      )}

      {operations.map((group, idx) => (
        <div key={idx}>
          <Typography variant="h6" m={2}>
            {group.header}
          </Typography>
          {group.items.map((item) => (
            <Link to={item.route} key={item.label}>
              <StyledButton variant="contained" color="primary">
                {item.label}
              </StyledButton>
            </Link>
          ))}
        </div>
      ))}

      <NotificationSnackbar
        message={snackbarMessage ?? ""}
        onClose={() => setSnackbarMessage(null)}
      />
    </Box>
  )
}

const getFormattedDate = (date: any) =>
  date ? new Date(date).toLocaleDateString() : "Loading..."
